import { isV2Form } from '@components/form-builder';
import { addFieldIdToForm } from '@components/form/dynamic-fields';
import { ROTATING_LOADING_MIN_DURATION } from '@components/rotating-loading-messages';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { createFieldNameToFieldIdMap, remapConditionProperties } from 'utils/prepare-form';

import { FormResponse, loadForm, loadJourneyForm, saveForm, saveJourneyForm } from './request';

export const useForm = (uuid: string) => {
  return useQuery({
    queryKey: ['form', uuid],
    staleTime: Infinity,
    queryFn: async () => {
      const data = await loadForm(uuid);

      if (isV2Form(data.fieldsConfig)) {
        return data;
      }

      // Make sure all fields have unique IDs
      const formWithIds = addFieldIdToForm(data.fieldsConfig);

      // Next we need to re-map field condition `name`s to their corresponding unique IDs
      const nameIdMap = createFieldNameToFieldIdMap(formWithIds);
      const remapped = remapConditionProperties(formWithIds, nameIdMap);

      return {
        ...data,
        fieldsConfig: remapped,
      } as FormResponse;
    },
  });
};

export const useJourneyForm = (journeyUuid: string, formUuid: string) => {
  return useQuery({
    queryKey: ['journey-form', journeyUuid, formUuid],
    staleTime: Infinity,
    queryFn: () => loadJourneyForm(journeyUuid, formUuid),
  });
};

export const useSaveForm = (uuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) => saveForm(uuid, formData),
    onSuccess: (data) => {
      if (data.data.isComplete === false) {
        queryClient.refetchQueries({ queryKey: ['form', uuid] });
      }
    },
  });
};

export const useSaveJourneyForm = (journeyUuid: string, formUuid: string) => {
  const { t } = useScopedTranslate(translations);
  return useMutation({
    mutationFn: (formData: FormData) => saveJourneyForm(journeyUuid, formUuid, formData),
    onError: (error: AxiosError<{ error?: string }>) => {
      const errorCode = error.response?.data?.error;
      const messages: Record<string, string> = {
        missing_required_fields: t('missingRequiredFields'),
        invalid_email: t('invalidEmail'),
      };
      const message = errorCode && messages[errorCode];
      if (!message) return;
      setTimeout(() => showError(message), ROTATING_LOADING_MIN_DURATION);
    },
  });
};

const translations = {
  en: {
    missingRequiredFields: 'Please fill in your name and email before submitting.',
    invalidEmail: 'Please enter a valid email address.',
  },
  is: {
    missingRequiredFields: 'Vinsamlegast fylltu út nafn og netfang áður en þú sendir.',
    invalidEmail: 'Vinsamlegast sláðu inn gilt netfang.',
  },
};
